// 创建账号
import { Form, Input, Row, Col, Select, Switch, Button, Divider } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import {
  EyeFilled,
  EyeInvisibleFilled,
  SafetyOutlined,
  StarFilled,
} from '@ant-design/icons';
import { useNavigation } from '@/common/hooks/navigation';
import VerifyEmilCode from './verifyEmilCode';
import countryCode from '@/constants/country';
import Login from '@/modules/auth/api/main';
import googleIcon from '@/assets/img/google-icon.svg';
import './index.scss';

// Inline styles
const googleButtonStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '44px',
  marginTop: '16px',
  border: '1px solid #ddd',
  backgroundColor: 'white',
  color: '#333',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
};

const googleIconStyle = {
  marginRight: '8px',
  width: '18px',
  height: '18px',
};

const dividerStyle = {
  margin: '20px 0',
  color: 'rgba(0, 0, 0, 0.45)',
  fontSize: '14px',
};

const securityWidgetStyle = {
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  padding: '12px 16px',
  border: '1px solid #e9ecef',
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  alignItems: 'center',
  gap: '12px',
  marginTop: '16px',
  marginBottom: '16px',
  // Mobile responsive styles
  '@media (max-width: 768px)': {
    padding: '10px 14px',
    gap: '10px',
  },
  '@media (max-width: 480px)': {
    padding: '8px 12px',
    gap: '8px',
  },
};

const securityIconStyle = {
  backgroundColor: '#10b981',
  color: '#ffffff',
  fontSize: '18px',
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
  // Mobile responsive
  '@media (max-width: 768px)': {
    width: '32px',
    height: '32px',
    fontSize: '16px',
  },
  '@media (max-width: 480px)': {
    width: '28px',
    height: '28px',
    fontSize: '14px',
  },
};

const widgetContentStyle = {
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  gap: '4px',
};

const widgetTitleStyle = {
  margin: 0,
  fontSize: '13px',
  fontWeight: '600',
  color: '#111827',
  lineHeight: '1.3',
  // Mobile responsive
  '@media (max-width: 768px)': {
    fontSize: '12px',
  },
  '@media (max-width: 480px)': {
    fontSize: '11px',
  },
};

const widgetSubtitleStyle = {
  margin: 0,
  fontSize: '11px',
  color: '#6b7280',
  lineHeight: '1.4',
  // Mobile responsive
  '@media (max-width: 768px)': {
    fontSize: '10px',
  },
  '@media (max-width: 480px)': {
    fontSize: '9px',
  },
};

const ratingSectionStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginTop: '3px',
  // Mobile responsive
  '@media (max-width: 768px)': {
    gap: '5px',
    marginTop: '2px',
  },
  '@media (max-width: 480px)': {
    gap: '4px',
    marginTop: '2px',
  },
};

const starsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '1px',
  lineHeight: '1',
};

const sectionDividerStyle = {
  margin: '16px 0',
  border: 'none',
  borderTop: '1px solid #e2e8f0',
  opacity: 0.6,
};

const accreditationSectionStyle = {
  backgroundColor: '#fafafa',
  margin: '16px -24px -24px',
  padding: '16px 24px',
  borderRadius: '0 0 12px 12px',
};

const { Option } = Select;

// Security and Accreditation Widget Component
const SecurityWidget = () => {
  // Check if mobile device
  const isMobile = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  };

  return (
    <div style={securityWidgetStyle}>
      <div style={securityIconStyle}>
        <SafetyOutlined />
      </div>

      <div style={widgetContentStyle}>
        <h3 style={widgetTitleStyle}>Plateforme sécurisée et accréditée</h3>
        <p style={widgetSubtitleStyle}>Régulée par l'ORIAS et l'AMF</p>

        <div style={ratingSectionStyle}>
          <div style={starsStyle}>
            {[...Array(5)].map((_, index) => (
              <StarFilled
                key={index}
                style={{
                  color: '#fbbf24',
                  fontSize: isMobile() ? '10px' : '12px',
                }}
              />
            ))}
          </div>
          <span
            style={{
              fontSize: isMobile() ? '9px' : '11px',
              color: '#6b7280',
              fontWeight: '500',
              lineHeight: '1',
            }}
          >
            5/5 Google Avis
          </span>
        </div>
      </div>
    </div>
  );
};

export default function CreateAccount(props) {
  const { onSwitch } = props;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState(true);
  const navigation = useNavigation();
  const [isVerifyEmilCode, setIsVerifyEmilCode] = useState(false);
  const [formValue, setFormValue] = useState({});
  const [agreed, setAgreed] = useState(false);

  const filterCountryCodes = useMemo(() => {
    return countryCode.map(item => {
      return {
        ...item,
        zone: `+${item.zone}`,
      };
    });
  }, []);

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Enter' && !disabled && agreed) {
        onSubmit();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [disabled, agreed]);

  const onChangeCode = () => {
    // Track country code selection
    trackFormInteraction(
      'country_code_change',
      'phone_code',
      form.getFieldValue('phone_code'),
    );

    form.setFieldValue('phone', '');
    // 清除校验错误信息
    form.resetFields(['phone']);
  };

  const prefixSelector = (
    <Form.Item name="phone_code" noStyle>
      <Select
        style={{ width: 90 }}
        className="prefixSelect"
        fieldNames={{ value: 'zone', label: 'zone' }}
        options={filterCountryCodes}
        onChange={onChangeCode}
      />
    </Form.Item>
  );

  const checkIfAllFieldsAreFilled = () => {
    const fields = form.getFieldsValue();
    return !Object.values(fields).every(value => value);
  };

  const onHandleSwitch = () => {
    // onSwitch && onSwitch('signIn');
    navigation.push('/login');
  };

  // 监听判断form里面每一个表单是否都填写了，如果全部填写了disabled为false，否则为true
  const onValuesChange = changedValues => {
    // Track form field changes
    const fieldName = Object.keys(changedValues)[0];
    const fieldValue = changedValues[fieldName];

    // Don't track password values for security
    if (fieldName !== 'password') {
    }

    setDisabled(checkIfAllFieldsAreFilled);
  };

  const onChangeStatus = () => {
    setIsVerifyEmilCode(false);
  };

  // 邮箱验证码校验成功回调
  const onFetchCode = (type, userInfo) => {
    // Track user registration completion
    if (userInfo && userInfo.id) {
      /*     Analytics.identify(userInfo.id, {
        email: userInfo.email,
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
        phone: userInfo.phone,
        signup_date: new Date().toISOString(),
      }); */
    }

    setIsVerifyEmilCode(false);
    onHandleSwitch();
  };

  // 提交表单
  const onSubmit = () => {
    form
      .validateFields()
      .then(values => {
        setLoading(true);

        Login.checkAccount({
          email: values.email,
          first_name: values.first_name,
        })
          .then(res => {
            // Check if response is an axios response object with data property
            const data = res.data || res;
            if (data.code === 2010) {
              console.log('Verification code sent:', data.message);
              setFormValue(values);
              setIsVerifyEmilCode(true);
            } else {
              console.log('Account check successful:', data);
            }
          })
          .catch(err => {
            console.error('Error during account check:', err);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(errorInfo => {
        // Track form validation errors
        console.log('errorInfo', errorInfo);
      });
  };

  const trimValue = e => {
    return e.target.value.trim();
  };

  const params = useMemo(() => {
    return {
      ...formValue,
      type: 1,
      origin: 'createAccount',
    };
  }, [formValue]);

  const onChangeAgreed = () => {
    // Track terms agreement change

    setAgreed(!agreed);
  };

  const toCheckAgreedLink = () => {
    window.open(
      'https://optivest.fr/conditions-generales-utilisation/',
      '_blank',
    );
  };

  // Handle Google sign up
  const handleGoogleSignUp = () => {
    // Redirect to Google auth endpoint
    Login.initiateGoogleAuth();
  };

  return (
    <>
      {!isVerifyEmilCode ? (
        <div className="loginPage_right_main loginPage_right_main_createAccount">
          <div className="tittle">Créer mon compte</div>
          <div className="switchEntryBtn" onClick={onHandleSwitch}>
            Déjà inscrit ?
          </div>

          <div className="formContent">
            <Form
              form={form}
              onValuesChange={onValuesChange}
              layout="vertical"
              className="createAccountForm"
              initialValues={{ phone_code: '+33' }}
            >
              <Row gutter={10}>
                <Col span={12}>
                  <Form.Item
                    validateTrigger={[]}
                    label="Prénom *"
                    name="first_name"
                    colon={false}
                    // getValueFromEvent={e => trimValue(e)}
                    rules={[{ required: true, message: '' }]}
                  >
                    <Input placeholder="Entrer..." />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Nom *"
                    name="last_name"
                    colon={false}
                    rules={[{ required: true, message: '' }]}
                    // getValueFromEvent={e => trimValue(e)}
                  >
                    <Input placeholder="Entrer..." />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label="Email *"
                name="email"
                colon={false}
                validateTrigger={['onBlur']}
                getValueFromEvent={e => trimValue(e)}
                rules={[
                  {
                    required: true,
                    type: 'email',
                    message: '',
                  },
                  {
                    message: "Format d'email incorrect",
                    pattern: /^\w+((-\w+)|(\.\w+))*\@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z0-9]+$/,
                  },
                ]}
              >
                <Input placeholder="Entrer..." />
              </Form.Item>
              <Form.Item
                label="Numéro de téléphone *"
                name="phone"
                colon={false}
                className="phoneNumberFormItem"
                validateTrigger={['onBlur']}
                getValueFromEvent={e => trimValue(e)}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  {
                    pattern: /^\d+$/,
                    message: 'Format de numéro incorrect',
                  },
                ]}
              >
                <Input
                  addonBefore={prefixSelector}
                  style={{ width: '100%' }}
                  placeholder="Entrer..."
                />
              </Form.Item>
              <Form.Item
                label="Mot de passe *"
                name="password"
                colon={false}
                className="passwordFormItem"
                getValueFromEvent={e => trimValue(e)}
                validateTrigger={['onBlur']}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  {
                    pattern: /^\S{6,}$/,
                    message: 'Au moins 6 caractères',
                  },
                ]}
              >
                <Input.Password
                  placeholder="Entrer..."
                  iconRender={visible =>
                    visible ? <EyeFilled /> : <EyeInvisibleFilled />
                  }
                />
              </Form.Item>
              <Form.Item
                label="Confirmer le mot de passe *"
                name="confirmPassword"
                colon={false}
                className="passwordFormItem"
                getValueFromEvent={e => trimValue(e)}
                validateTrigger={['onBlur']}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  {
                    validator: (rule, value) => {
                      const test = /^\S{6,}$/;
                      if (!test.test(value)) {
                        return Promise.reject('Au moins 6 caractères');
                      }
                      if (value !== form.getFieldValue('password')) {
                        return Promise.reject(
                          'Les deux mots de passe ne correspondent pas',
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input.Password
                  placeholder="Entrer..."
                  iconRender={visible =>
                    visible ? <EyeFilled /> : <EyeInvisibleFilled />
                  }
                />
              </Form.Item>

              {/* Security Widget */}
              <SecurityWidget />

              <div className="agreedWrap">
                <Switch
                  checked={agreed}
                  onChange={onChangeAgreed}
                  onKeyDown={e => e.preventDefault()}
                />
                <span className="agreedText">
                  En cliquant sur ce bouton, vous acceptez les{' '}
                  <span className="agreedLinkText" onClick={toCheckAgreedLink}>
                    Conditions générales d{"'"}utilisation
                  </span>
                </span>
              </div>
            </Form>
            <div className="createAccountBox">
              <Button
                disabled={disabled || !agreed}
                loading={loading}
                onClick={onSubmit}
                className="createBtn"
              >
                CRÉER MON COMPTE
              </Button>
            </div>

            {/* Google Sign In Button */}
            <Divider style={dividerStyle}>ou</Divider>

            <Button
              style={googleButtonStyle}
              block
              onClick={handleGoogleSignUp}
              icon={
                <img src={googleIcon} alt="Google" style={googleIconStyle} />
              }
            >
              S'inscrire avec Google
            </Button>
          </div>
        </div>
      ) : (
        <VerifyEmilCode
          params={params}
          onSwitch={onChangeStatus}
          onFetchCodeCallBack={onFetchCode}
        />
      )}
    </>
  );
}
